/**
 * SA-1127: the Inbox toolbar's one toggle chip: a pill on the neutral wash
 * with aria-pressed; on, an outline in primary and a check before the label
 * (SA-1123: a selected state is an outline, never a filled primary pill).
 * It forwards its ref and every button attribute, so a Tip can wrap it.
 */
import { forwardRef, type ButtonHTMLAttributes, type ComponentType, type ReactNode } from 'react'
import { Check } from 'lucide-react'
import { Button } from '@/components/ui'
import { cn } from '@/utils'

export const CHIP_CLASS =
  'h-7 rounded-full border border-transparent bg-muted px-2.5 text-xs font-medium text-muted-foreground hover:bg-accent hover:text-foreground dark:hover:bg-accent'
export const CHIP_ON_CLASS = 'border-primary text-primary hover:text-primary'

export interface FilterChipProps extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'children'> {
  on: boolean
  icon?: ComponentType<{ className?: string; 'aria-hidden'?: boolean | 'true' | 'false' }>
  children: ReactNode
}

export const FilterChip = forwardRef<HTMLButtonElement, FilterChipProps>(function FilterChip(
  { on, icon: Icon, children, className, type = 'button', ...rest },
  ref,
) {
  return (
    <Button
      ref={ref}
      type={type}
      variant="ghost"
      size="sm"
      aria-pressed={on}
      className={cn(CHIP_CLASS, on && CHIP_ON_CLASS, className)}
      {...rest}
    >
      {on && <Check className="size-3" aria-hidden="true" data-testid="chip-check" />}
      {Icon && <Icon className="size-3" aria-hidden="true" />}
      {children}
    </Button>
  )
})
